import findLast from 'lodash/findLast';
import type { PastTimeSeriesForCalculations } from '../../types';
import { isMostRecentReportedYearInYearlyTimeSeries } from '../../utils/pastAnnualRevenueEarnings';
import {
  DEFAULT_SHARES_PERCENTAGE_FLOOR,
  DEFAULT_SHARES_PERCENTAGE_CEILING,
} from './constants';

export const calculateSharesOutstandingGrowthRate = ({
  sharesOutstanding,
  previousYearSharesOutstanding,
}: {
  sharesOutstanding: number;
  previousYearSharesOutstanding: number;
}) => {
  return sharesOutstanding / previousYearSharesOutstanding - 1;
};

export const getPreviousYearSharesOutstanding = ({
  mostRecentReportedYear,
  pastTimeSeriesForCalculations,
}: {
  mostRecentReportedYear: string;
  pastTimeSeriesForCalculations?: PastTimeSeriesForCalculations;
}) => {
  const previousYear = String(Number(mostRecentReportedYear) - 1);

  const previousYearSharesOutstanding =
    isMostRecentReportedYearInYearlyTimeSeries({
      mostRecentReportedYear: previousYear,
      pastTimeSeriesModel: pastTimeSeriesForCalculations,
    })
      ? Number(
          pastTimeSeriesForCalculations?.yearlyTimeSeries[previousYear]
            .totalSharesOutstanding
        )
      : 0; // Assuming migration of legacy fair value and so should always have fiscal data for editing or viewing

  return previousYearSharesOutstanding;
};

const findLatestSharesOutstanding = (
  pastTimeSeriesForCalculations?: PastTimeSeriesForCalculations
) => {
  return (
    findLast(
      pastTimeSeriesForCalculations?.yearlyTimeSeries,
      (year) => year.totalSharesOutstanding !== 0
    )?.totalSharesOutstanding ?? 1
  );
};

export const getMostRecentTotalSharesOutstanding = ({
  mostRecentReportedYear,
  pastTimeSeriesForCalculations,
}: {
  mostRecentReportedYear: string;
  pastTimeSeriesForCalculations?: PastTimeSeriesForCalculations;
}) => {
  const presentSharesOutstanding = isMostRecentReportedYearInYearlyTimeSeries({
    mostRecentReportedYear,
    pastTimeSeriesModel: pastTimeSeriesForCalculations,
  })
    ? Number(
        pastTimeSeriesForCalculations?.yearlyTimeSeries[mostRecentReportedYear]
          .totalSharesOutstanding
      )
    : 0;

  if (Number(presentSharesOutstanding) === 0) {
    return findLatestSharesOutstanding(pastTimeSeriesForCalculations) ?? 1;
  }

  return presentSharesOutstanding;
};

export const getCappedSharesOutstandingGrowthRate = (
  sharesOutstandingGrowthRate: number
) => {
  if (sharesOutstandingGrowthRate <= DEFAULT_SHARES_PERCENTAGE_FLOOR)
    return DEFAULT_SHARES_PERCENTAGE_FLOOR;
  if (sharesOutstandingGrowthRate >= DEFAULT_SHARES_PERCENTAGE_CEILING)
    return DEFAULT_SHARES_PERCENTAGE_CEILING;
  return sharesOutstandingGrowthRate;
};

export const isValidRangeOfSharesOutstandingGrowthRate = (
  sharesOutstandingGrowthRate: number
) => {
  return (
    sharesOutstandingGrowthRate >= DEFAULT_SHARES_PERCENTAGE_FLOOR &&
    sharesOutstandingGrowthRate <= DEFAULT_SHARES_PERCENTAGE_CEILING
  );
};
